import { ethers } from "ethers";
import type { Dispatch } from "redux";

export interface PermitTypedDataField {
  name: string;
  type: string;
}

/**
 * EIP-712 types for the Permit struct as specified by EIP-2612.
 * The permit signature is encoded and signed using EIP-712.
 */
export const PERMIT_TYPES: { Permit: PermitTypedDataField[] } = {
  Permit: [
    { name: "owner", type: "address" },
    { name: "spender", type: "address" },
    { name: "value", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

export interface PermitCurrencyDomain {
  name: string;
  version: string;
  address: string;
}

/** Params for dispatching the permit sign action */
export interface DispatchPermitSignParams {
  userAddress: string;
  spenderAddress: string;
  amount: string | bigint;
  nonce: number;
  chainId: number;
  currency: PermitCurrencyDomain;
}

interface EthEip712SignAction {
  type: "ETH_EIP_712_SIGN";
  domain: {
    name: string;
    version: string;
    chainId: number;
    verifyingContract: string;
  };
  types: typeof PERMIT_TYPES;
  value: {
    owner: string;
    spender: string;
    value: bigint;
    nonce: number;
    deadline: number;
  };
}

/**
 * Dispatches EIP-2612 permit sign request (EIP-712 encoded) for deposit.
 * User signs off-chain (no gas); then PolicyPool.depositWithPermit can be called in one tx.
 * Domain and Permit struct must match the currency token's DOMAIN_SEPARATOR.
 */
export function dispatchPermitSign(dispatch: Dispatch<EthEip712SignAction>, params: DispatchPermitSignParams): void {
  const { userAddress, spenderAddress, amount, nonce, chainId, currency } = params;
  const deadline = Math.floor(Date.now() / 1000) + 60 * 60; // 1 hour
  const domain = {
    name: currency.name,
    version: currency.version,
    chainId,
    verifyingContract: currency.address,
  };
  const value = {
    owner: ethers.getAddress(userAddress),
    spender: ethers.getAddress(spenderAddress),
    value: typeof amount === "bigint" ? amount : BigInt(amount),
    nonce: Number(nonce),
    deadline,
  };
  dispatch({
    type: "ETH_EIP_712_SIGN",
    domain,
    types: PERMIT_TYPES,
    value,
  });
}
